<template>
  <div v-if="planSale">
    <div>
      <!-- Page Header -->
      <ShowPageHeader backLabel="Contrats" backRoute="/plan-sales" :title="planSale.friendlyId">
        <template #badges>
          <FieldSpan :value="planSale.category" style-to-add="bg-blue-100 text-blue-800" />
          <FieldSpan
            :value="planSale.status"
            :style-to-add="planSaleStatusClass"
            translation-prefix="planSale.status"
          />
        </template>
        <template #actions>
          <!-- Renewal Button and Modal -->
          <div v-if="renewalInformations?.renewalAvailable && renewalInformations?.expiredOrExpiringSoon">
            <SecondaryButton @click="openRenewalModal = true">Renouveler</SecondaryButton>
            <Modal :open="openRenewalModal" @close="openRenewalModal = false">
              <div class="flex justify-between items-center">
                <p>{{ $t("Generate and copy the renewal link") }}</p>
                <SecondaryButton @click="getRenewPlanSaleLink">Générer</SecondaryButton>
              </div>
              <div class="flex justify-between items-center mt-10">
                <p>{{ $t("Send the renewal link directly to the client") }}</p>
                <SecondaryButton @click="renewPlanSale">Envoyer</SecondaryButton>
              </div>
            </Modal>
          </div>

          <!-- Update Button and Modal -->
          <div v-if="planSaleIsUpdatable">
            <SecondaryButton @click="openUpdateModal = true">Modifier</SecondaryButton>
            <UpdateModal
              :open="openUpdateModal"
              :planSale="planSale"
              @closeUpdateModal="openUpdateModal = false"
              @updatePlanSale="updatePlanSale"
            />
          </div>

          <!-- Exchange Product Button and Modal -->
          <div v-if="isProductExchangeAllowed">
            <SecondaryButton @click="openExchangeModal = true">Échanger le produit</SecondaryButton>
            <ProductExchangeFormModal
              :open="openExchangeModal"
              :planSale="planSale"
              @close="openExchangeModal = false"
              @refreshPlanSale="getPlanSale"
            />
          </div>

          <!-- Cancellation Button and Modal -->
          <div v-if="!planSaleIsCancelled">
            <SecondaryButton @click="openModal = true">Résilier</SecondaryButton>
            <CancellationModal
              :open="openModal"
              :plan-sale="planSale"
              @close="openModal = false"
              @cancelPlanSale="cancelPlanSale"
            />
          </div>

          <!-- Reactivation Button and Modal -->
          <div v-if="planSale.reactivable">
            <SecondaryButton @click="openReactivationModal = true">
              {{ $t("planSale.reactivation.button") }}
            </SecondaryButton>
            <ReactivationModal
              :open="openReactivationModal"
              @close="openReactivationModal = false"
              @reactivatePlanSale="reactivatePlanSale"
            />
          </div>

          <!-- Send Confirmation Email Button -->
          <div v-if="!planSaleIsCancelled">
            <SecondaryButton @click="sendConfirmationEmail">Envoyer le mail de confirmation</SecondaryButton>
          </div>

          <SecondaryButton @click="CreateModalOpen = true">Créer une réclamation</SecondaryButton>
        </template>
      </ShowPageHeader>

      <EmailAutoAlert
        v-if="planSale.customer"
        :email-filled-automatically="planSale.customer.emailFilledAutomatically"
        class="mt-4"
      />

      <!-- Plan Sale Summary Card -->
      <div class="mt-6">
        <div class="bg-white rounded-2xl shadow-md p-6 mx-auto">
          <!-- Plan Sale Details -->
          <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-2">
            <!-- Column 1 - Produit -->
            <div class="space-y-2">
              <StackedField label="Formule" :value="planSale.title" />
              <StackedField label="Prix" :value="planSalePrice" />
              <StackedField label="Paiement" :value="planSale.monthly ? 'Mensuel' : 'Annuel'" />
              <StackedField
                v-if="planSale.orderReferenceId"
                label="Commande marchand"
                :value="planSale.orderReferenceId"
              />
            </div>

            <!-- Column 2 - Commercial -->
            <div class="space-y-2">
              <StackedField label="Offre">
                <InlineLink @click="goToContractDistributionDetails">
                  {{ planSale.category }}
                </InlineLink>
              </StackedField>
              <StackedField label="Boutique">
                <InlineLink @click="goToStoreDetails">
                  {{ planSale.store?.salesChannel }}
                </InlineLink>
              </StackedField>
              <StackedField v-if="sellerName" label="Vendeur">
                <div class="flex space-x-2">
                  <p>{{ sellerName }}</p>
                  <button type="button" aria-label="Modifier le vendeur" @click="showUpdateSellerModal">
                    <PencilIcon class="h-5 cursor-pointer" />
                  </button>
                </div>
              </StackedField>
            </div>

            <!-- Column 3 - Contrat & Dates -->
            <div class="space-y-2">
              <StackedField label="Début du contrat" :value="planSale.date" />
              <StackedField label="Fin du contrat" :value="planSale?.cancellation?.date" />
              <StackedField label="Fin du contrat théorique" :value="planSale.expirationDate" />
              <StackedField label="Engagement" :value="cancellationPolicyTypeLabel" />
            </div>
          </div>

          <!-- Cancellation Information -->
          <Cancellation :cancellation="cancellation" v-if="isPresent(cancellation)" />
          <ScheduledCancellation
            :scheduledCancellation="scheduledCancellation"
            v-else-if="isPresent(scheduledCancellation)"
          />

          <!-- Membership Agreement Download -->
          <div v-if="planSale.membershipAgreement" class="mt-6 pt-4 border-t border-gray-200">
            <LinkField label="Certificat d'adhésion" :href="planSale.membershipAgreement" />
          </div>
        </div>
      </div>

      <!-- Tabs Navigation -->
      <TabsNavigation :tabs="tabsWithBadge" class="my-6" />

      <!-- Tab Content: Products -->
      <div v-if="currentTab === 'products'" class="mt-6">
        <InsuredProductsInformations v-if="planSale.products" :products="planSale.products" />
      </div>

      <!-- Tab Content: Customer -->
      <div v-if="currentTab === 'customer'" class="mt-6 flex flex-col lg:flex-row gap-4">
        <CustomerInformations :customer="planSale.customer" class="flex-1 min-w-0" />
        <IntercomConversationList
          v-if="planSale.customer?.email"
          :customer-email="planSale.customer.email"
          class="lg:w-80 xl:w-96 shrink-0"
        />
      </div>

      <!-- Tab Content: Payments -->
      <div v-if="currentTab === 'payments'" class="mt-6 space-y-4">
        <PaymentMethod v-if="planSale.subscriptionReferenceId" :plan-sale="planSale" />
        <PlanSaleBillingDetails
          :planSaleBillings="planSale.planSaleBillings"
          :chargedBy="planSale.chargedBy"
          :subscriptionReferenceId="planSale.subscriptionReferenceId"
        />
      </div>

      <div v-if="currentTab === 'retributions'" class="mt-6">
        <RetributionDetails :retribution="retribution" />
      </div>

      <!-- Tab Content: Claims -->
      <div v-if="currentTab === 'claims'" class="mt-6">
        <ClaimsIndex :planSaleFriendlyId="planSale.friendlyId" />
      </div>

      <!-- Tab Content: Contract updates -->
      <div v-if="currentTab === 'contract_updates'" class="mt-6">
        <ContractUpdateList
          :contractUpdates="planSale.contractUpdates"
          :formulaChangeLetter="planSale.formulaChangeLetter"
        />
      </div>

      <div v-if="currentTab === 'product_updates'" class="mt-6">
        <ProductUpdateList :productUpdates="planSale.productUpdates" />
      </div>
    </div>

    <CreateModal :open="CreateModalOpen" :planSaleId="planSale.friendlyId" @close="CreateModalOpen = false" />
    <UpdateSellerModal
      :open="openUpdateSellerModal"
      :planSale="planSale"
      @close="openUpdateSellerModal = false"
      @updated="onSellerUpdated"
    />
  </div>
</template>

<script>
// UI Components
import {
  InlineLink,
  StackedField,
  FieldSpan,
  LinkField,
  Modal,
  SecondaryButton,
  ShowPageHeader,
  TabsNavigation,
  formatPrice,
  isPresent,
} from "@estaly/ui";

// Custom Components
import PlanSaleBillingDetails from "@/components/plan_sale/PlanSaleBillingDetails.vue";
import Cancellation from "@/components/plan_sale/Cancellation.vue";
import ScheduledCancellation from "@/components/plan_sale/ScheduledCancellation.vue";
import CancellationModal from "@/components/plan_sale/CancellationModal.vue";
import ReactivationModal from "@/components/plan_sale/ReactivationModal.vue";
import UpdateModal from "@/components/plan_sale/UpdateModal.vue";
import UpdateSellerModal from "@/components/plan_sale/UpdateSellerModal.vue";
import InsuredProductsInformations from "@/components/plan_sale/InsuredProductsInformations.vue";
import CustomerInformations from "@/components/plan_sale/CustomerInformations.vue";
import EmailAutoAlert from "@/components/plan_sale/EmailAutoAlert.vue";
import ClaimsIndex from "@/components/claim/details/information/ClaimsIndex.vue";
import CreateModal from "@/components/complaints/CreateModal.vue";
import IntercomConversationList from "@/components/intercom/IntercomConversationList.vue";

// Utilities
// Vuex
import { mapActions, mapGetters } from "vuex";
import RetributionDetails from "@/components/plan_sale/RetributionDetails.vue";
import { PencilIcon } from "@heroicons/vue/outline";
import PaymentMethod from "@/components/plan_sale/PaymentMethod.vue";
import ContractUpdateList from "@/components/plan_sale/ContractUpdateList.vue";
import ProductUpdateList from "@/components/plan_sale/ProductUpdateList.vue";
import ProductExchangeFormModal from "@/components/plan_sale/ProductExchangeFormModal.vue";

export default {
  name: "PlanSaleDetails",

  components: {
    ProductUpdateList,
    ProductExchangeFormModal,
    PaymentMethod,
    PencilIcon,
    RetributionDetails,
    // UI Components
    InlineLink,
    Modal,
    TabsNavigation,
    StackedField,
    FieldSpan,
    SecondaryButton,
    ShowPageHeader,
    LinkField,

    // Plan Sale Components
    PlanSaleBillingDetails,
    Cancellation,
    ScheduledCancellation,
    CancellationModal,
    ReactivationModal,
    UpdateModal,
    UpdateSellerModal,
    InsuredProductsInformations,
    CustomerInformations,
    EmailAutoAlert,
    ContractUpdateList,

    // Claims Components
    ClaimsIndex,
    CreateModal,

    // Intercom
    IntercomConversationList,
  },

  data: () => {
    return {
      planSale: null,
      cancellation: null,
      scheduledCancellation: null,
      openModal: false,
      CreateModalOpen: false,
      openUpdateModal: false,
      openRenewalModal: false,
      openUpdateSellerModal: false,
      openExchangeModal: false,
      openReactivationModal: false,
    };
  },

  beforeMount() {
    this.getPlanSale();
  },

  computed: {
    ...mapGetters("planSales", [
      "productExchangeAllowed",
      "isCancelled",
      "isUpdatable",
      "statusBadgeClass",
      "hasFailedPayments",
    ]),

    planSalePrice() {
      return `${formatPrice(this.planSale.price)}${this.planSale.monthly ? " / mois" : ""}`;
    },

    isProductExchangeAllowed() {
      return this.productExchangeAllowed(this.planSale, this.planSale.claims);
    },

    // Tab Management
    currentTab() {
      return this.$route.query.tab || "customer";
    },

    tabsWithBadge() {
      const baseTabs = [
        { name: "customer", displayName: "Client", query: { tab: "customer" } },
        { name: "payments", displayName: "Paiements", query: { tab: "payments" } },
        { name: "claims", displayName: "Sinistres", query: { tab: "claims" } },
        this.planSale.products && {
          name: "products",
          displayName: "Produits",
          query: { tab: "products" },
        },
        this.retribution && { name: "retributions", displayName: "Primes", query: { tab: "retributions" } },
        this.planSale.contractUpdates.length > 0 && {
          name: "contract_updates",
          displayName: "Changement de formule",
          query: { tab: "contract_updates" },
        },
        this.planSale.productUpdates.length > 0 && {
          name: "product_updates",
          displayName: "Echanges de produit",
          query: { tab: "product_updates" },
        },
      ].filter(Boolean);

      // Add red badge on "Payments" tab if there are failed payments
      if (this.planSaleHasFailedPayments) {
        const paymentsTab = baseTabs.find((tab) => tab.name === "payments");
        if (paymentsTab) {
          paymentsTab.badge = {
            text: "!",
            class: "bg-red-100 text-red-800",
          };
        }
      }

      return baseTabs;
    },

    // Plan Sale Status
    planSaleIsCancelled() {
      return this.isCancelled(this.planSale);
    },

    planSaleIsUpdatable() {
      return this.isUpdatable(this.planSale);
    },

    retribution() {
      return this.planSale?.planSaleBillings?.[0]?.retribution;
    },

    planSaleStatusClass() {
      return this.statusBadgeClass(this.planSale);
    },

    // Plan Sale Data
    sellerName() {
      return [this.planSale.seller?.firstName, this.planSale.seller?.lastName].filter(Boolean).join(" ");
    },

    renewalInformations() {
      return this.planSale?.renewalInformations;
    },

    planSaleHasFailedPayments() {
      return this.hasFailedPayments(this.planSale);
    },

    cancellationPolicyTypeLabel() {
      const type = this.planSale?.cancellationPolicyType;
      if (!type) return null;

      return this.$t(`planSale.cancellationPolicyType.${type}`);
    },
  },

  methods: {
    // Vuex Actions
    ...mapActions("notifications", ["notify"]),
    ...mapActions("backoffice", ["withLoader"]),
    ...mapActions("planSales", [
      "fetch",
      "cancel",
      "reactivate",
      "updateDetails",
      "getRenewLink",
      "renew",
      "sendConfirmation",
    ]),

    // External Utilities
    isPresent,
    // Navigation Methods
    goToStoreDetails() {
      if (this.planSale.storeId) this.$router.push(`/stores/${this.planSale.storeId}`);
    },

    goToContractDistributionDetails() {
      if (this.planSale.contractDistribution?.id)
        this.$router.push(`/contract-distributions/${this.planSale.contractDistribution.id}`);
    },

    // Data Fetching
    async getPlanSale() {
      const includes = [
        "renewal_informations",
        "seller",
        "cancellation",
        "plan_sale_billings",
        "store",
        "contract_distribution",
        "scheduled_cancellation",
        "customer",
        "plan_sale_billings.commissions",
        "plan_sale_billings.retribution",
        "plan_sale_product",
        "membership_agreement",
        "formula_change_letter",
        "contract_updates",
        "product_updates",
        "claims",
      ];

      this.planSale = await this.fetch({ id: this.$route.params.id, includes });
      this.cancellation = this.planSale.cancellation;
      this.scheduledCancellation = this.planSale.scheduledCancellation;
    },

    // Utility Methods
    copyToClipboard(textToCopy) {
      navigator.clipboard.writeText(textToCopy).then(() => {});
    },

    // Plan Sale Actions
    async cancelPlanSale(cancellationReason, feedbackMessage, cancellationDate) {
      await this.withLoader(async () => {
        const { success } = await this.cancel({
          planSaleId: this.planSale.friendlyId,
          cancellationReason,
          feedbackMessage,
          cancellationDate,
        });
        if (success) {
          this.openModal = false;
          this.notify({
            category: "simple",
            type: "success",
            title: "This plan sale has been cancelled.",
          });
          await this.getPlanSale();
        }
      });
    },

    async reactivatePlanSale() {
      await this.withLoader(async () => {
        const { success } = await this.reactivate({ planSaleId: this.planSale.friendlyId });
        if (success) {
          this.openReactivationModal = false;
          this.notify({
            category: "simple",
            type: "success",
            title: this.$t("planSale.reactivation.success"),
          });
          await this.getPlanSale();
        }
      });
    },

    async updatePlanSale(updatePlanSaleInformations) {
      await this.withLoader(async () => {
        const { success } = await this.updateDetails({
          planSaleId: this.planSale.friendlyId,
          planSaleInformations: updatePlanSaleInformations,
        });
        if (success) {
          this.openUpdateModal = false;
          this.notify({
            category: "simple",
            type: "success",
            title: "This plan sale has been updated.",
          });
          await this.getPlanSale();
        }
      });
    },

    // Seller Methods
    showUpdateSellerModal() {
      this.openUpdateSellerModal = true;
    },

    async onSellerUpdated() {
      await this.getPlanSale();
    },

    // Renewal Methods
    async getRenewPlanSaleLink() {
      const data = await this.getRenewLink({ planSaleId: this.planSale.friendlyId });
      if (!data?.planSaleRenewalLink) return;

      this.copyToClipboard(data.planSaleRenewalLink);
      await this.notify({
        category: "simple",
        type: "success",
        title: "Link copied to clipboard!",
      });
      this.openRenewalModal = false;
    },

    async renewPlanSale() {
      const { success } = await this.renew({ planSaleId: this.planSale.friendlyId });
      if (!success) return;

      await this.notify({
        category: "simple",
        type: "success",
        title: "Email successfully sent!",
      });
      this.openRenewalModal = false;
    },

    // Email Methods
    async sendConfirmationEmail() {
      if (!confirm("Envoyer le mail de confirmation de souscription ?")) return;

      const { success } = await this.sendConfirmation({ planSaleId: this.planSale.friendlyId });
      if (!success) return;

      await this.notify({
        category: "simple",
        type: "success",
        title: "Email envoyé",
      });
    },
  },
};
</script>
